import Head from 'next/head'
import { NextSeo } from 'next-seo'
import { Grid, Text, Title } from '@mantine/core'
import Link from 'next/link'
import pluralize from 'pluralize'

import * as Types from '../declarations'

import PageTemplate from './PageTemplate'
import CategoryPageTripRouteMap from './CategoryPageTripRouteMap'
import TrekCard from './TrekCard'
import JustifiedGallery from './TrekPage/components/JustifiedGallery'
import CountryBadge from 'components/CountryBadge'

import { sluggify, tripPageTripToTripCard } from '../lib/helper'
import { formatMonthNumbers, getCountryRoute } from '../lib/display'
import * as CONSTS from 'consts'
import HTMLContent from 'components/HTMLContent'

interface Props {
	version: Types.VERSION
	seo: {
		title: string
		headerTitle: string
		description: string
		canonical: string
		siteName: string
		images: {
			url: string
			width: number
			height: number
			alt: string
			type: string
		}[]
	}
	category: string
	categoryDeSlugged: string
	uniqueBestMonths: number[]
	trips: Types.DB.Models.TripAttributes[]
	allUploads: Types.DB.Models.UploadAttributes[]
}

const ContinentPageTemplate = ({
	seo,
	version,
	category,
	categoryDeSlugged,
	uniqueBestMonths,
	trips,
	allUploads,
}: Props) => {
	const continent =
		version === Types.VERSION.PEAKS
			? CONSTS.regions
					.flat()
					.find((region) => sluggify(region.continent) === category)
			: undefined
	console.log('ContinentPageTemplate', { continent, category })
	const countryCodes: string[] = continent?.countries.map(
		({ isoCode }) => isoCode,
	) as string[]

	return (
		<>
			<Head>
				<title>{seo.title}</title>

				<meta name="description" content={seo.description} />
				<link rel="canonical" href={seo.canonical} />
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<Title order={1} mb="lg">
					{seo.headerTitle}
				</Title>

				{!!continent?.blurb && (
					<Text mb="xl">
						<HTMLContent html={continent?.blurb as string} />
					</Text>
				)}

				<Grid mt="lg" mb="xl">
					<Grid.Col className="trip-map-col" span={{ base: 12, sm: 6 }}>
						{uniqueBestMonths.length > 0 && (
							<Text>
								The best time to go{' '}
								{version === Types.VERSION.TREKKING ? 'trekking' : 'climbing'}{' '}
								in {categoryDeSlugged} is:
								<ul>
									{formatMonthNumbers(uniqueBestMonths).map(
										(monthRange, monthRangeIndex) => (
											<li key={monthRangeIndex}>{monthRange}</li>
										),
									)}
								</ul>
							</Text>
						)}
						{!!countryCodes && (
							<>
								<Text mb="lg">
									There are expeditions in {countryCodes.length}{' '}
									{pluralize('country', countryCodes.length)}:
								</Text>

								<div
									style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}
								>
									{countryCodes.map((isoCode, idxCountry) => (
										<Link
											key={idxCountry}
											href={getCountryRoute(
												version === Types.VERSION.PEAKS
													? Types.App.DataTypes.TrekOrClimb.CLIMB
													: Types.App.DataTypes.TrekOrClimb.TREK,
												category,
												isoCode,
											)}
										>
											<CountryBadge isoCode={isoCode} />
										</Link>
									))}
								</div>
							</>
						)}
					</Grid.Col>
					<Grid.Col span={{ base: 12, sm: 6 }}>
						{trips.length > 1 && (
							<>
								<CategoryPageTripRouteMap trips={trips} alt={seo.title} />
							</>
						)}
					</Grid.Col>
				</Grid>

				<Title order={2} mt="xl" mb="lg">
					{version === Types.VERSION.TREKKING ? 'Treks' : 'Climbs'} in{' '}
					{categoryDeSlugged}
				</Title>
				{!!continent?.climbBlurb && (
					<Text mb="md">
						<HTMLContent html={continent?.climbBlurb as string} />
					</Text>
				)}
				<Text mb="xl">
					There are {trips.length}{' '}
					{version === Types.VERSION.TREKKING ? 'treks' : 'expeditions'} in{' '}
					{categoryDeSlugged}:
				</Text>
				<div id="trip-items" className="trek-card-container">
					{trips?.map((trip, key) => (
						<TrekCard tripCardProps={tripPageTripToTripCard(trip)} key={key} />
					))}
				</div>

				{allUploads.length > 0 && (
					<>
						<Title order={2} mt="xl">
							Pictures from{' '}
							{version === Types.VERSION.TREKKING ? 'trekking' : 'climbing'} in{' '}
							{categoryDeSlugged}
						</Title>
						<Text mt="lg">
							These pictures are taken while climbing in {categoryDeSlugged}. To
							see more pictures, view each country and better - specific trip
							pages. They&apos;re shown here to give you an idea of what
							conditions are like. Uploaded by users of the site.
						</Text>
						<JustifiedGallery
							pictures={allUploads}
							size="small"
							altText={seo.headerTitle}
						/>
					</>
				)}
			</PageTemplate>
		</>
	)
}

export default ContinentPageTemplate
